import { useState, useEffect } from 'react';
import { projectsApi } from '../../../services/api';
import { api } from '../../../services/api';
import type { WiringDiagram } from './types';
import { 
  Button, 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogFooter, 
  DialogTitle, 
  DialogDescription,
  Input,
  Label,
  Textarea,
} from '@/components/ui';
import { logger } from '../../../utils/logger';

interface DiagramsTabProps {
  projectId: string;
  onOpenDiagram?: (diagram: {
    id: string;
    diagramId: string;
    projectId: string;
    title: string;
  }) => void;
  diagrams: WiringDiagram[];
  setDiagrams: (diagrams: WiringDiagram[]) => void;
}

export function DiagramsTab({ projectId, onOpenDiagram, diagrams, setDiagrams }: DiagramsTabProps) {
  const [loadingDiagrams, setLoadingDiagrams] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  
  const [showNewDiagramModal, setShowNewDiagramModal] = useState(false);
  const [newDiagramName, setNewDiagramName] = useState('');
  const [newDiagramDescription, setNewDiagramDescription] = useState('');
  const [newDiagramStyle, setNewDiagramStyle] = useState<'boeing_ata' | 'airbus_ata' | 'default'>('boeing_ata');
  const [creatingDiagram, setCreatingDiagram] = useState(false);
  
  const [showDeleteDiagramModal, setShowDeleteDiagramModal] = useState(false);
  const [showRenameDiagramModal, setShowRenameDiagramModal] = useState(false);
  const [diagramToAction, setDiagramToAction] = useState<WiringDiagram | null>(null);
  const [renameDiagramName, setRenameDiagramName] = useState('');
  const [isDeletingDiagram, setIsDeletingDiagram] = useState(false);
  const [isRenamingDiagram, setIsRenamingDiagram] = useState(false);
  const [isDuplicatingDiagram, setIsDuplicatingDiagram] = useState(false);

  useEffect(() => {
    if (hasLoaded) return;
    const load = async () => {
      setLoadingDiagrams(true);
      try {
        const projectDiagrams = await projectsApi.listDiagrams(projectId);
        setDiagrams(projectDiagrams);
        setHasLoaded(true);
      } catch (err) {
        logger.error('Failed to load diagrams:', err);
      } finally {
        setLoadingDiagrams(false);
      }
    };
    load();
  }, [projectId, hasLoaded, setDiagrams]);

  const handleDiagramClick = (diagram: WiringDiagram) => {
    if (onOpenDiagram) {
      onOpenDiagram({
        id: diagram.diagramId,
        diagramId: diagram.diagramId,
        projectId,
        title: diagram.documentTitle,
      });
    }
  };

  const handleCreateDiagram = async () => {
    if (!newDiagramName.trim()) return;
    
    setCreatingDiagram(true);
    try {
      const diagram = await projectsApi.createDiagram(projectId, newDiagramName.trim(), newDiagramDescription.trim(), newDiagramStyle);
      setDiagrams([...diagrams, diagram]);
      setShowNewDiagramModal(false);
      setNewDiagramName('');
      setNewDiagramDescription('');
      setNewDiagramStyle('boeing_ata');
      
      if (onOpenDiagram) {
        onOpenDiagram({
          id: diagram.diagramId,
          diagramId: diagram.diagramId,
          projectId,
          title: diagram.documentTitle,
        });
      }
    } catch (err) {
      logger.error('Failed to create diagram:', err);
    } finally {
      setCreatingDiagram(false);
    }
  };

  const handleDeleteDiagram = async () => {
    if (!diagramToAction) return;
    
    setIsDeletingDiagram(true);
    try {
      await api.delete(`/projects/${projectId}/diagrams/${diagramToAction.diagramId}`);
      setDiagrams(diagrams.filter(d => d.diagramId !== diagramToAction.diagramId));
      setShowDeleteDiagramModal(false);
      setDiagramToAction(null);
    } catch (err) {
      logger.error('Failed to delete diagram:', err);
    } finally {
      setIsDeletingDiagram(false);
    }
  };

  const handleRenameDiagram = async () => {
    if (!diagramToAction || !renameDiagramName.trim()) return;
    
    setIsRenamingDiagram(true);
    try {
      await api.put(`/projects/${projectId}/diagrams/${diagramToAction.diagramId}`, {
        name: renameDiagramName.trim()
      });
      setDiagrams(diagrams.map(d => 
        d.diagramId === diagramToAction.diagramId 
          ? { ...d, documentTitle: renameDiagramName.trim() }
          : d
      ));
      setShowRenameDiagramModal(false);
      setDiagramToAction(null);
      setRenameDiagramName('');
    } catch (err) {
      logger.error('Failed to rename diagram:', err);
    } finally {
      setIsRenamingDiagram(false);
    }
  };

  const handleDuplicateDiagram = async (diagram: WiringDiagram) => {
    setIsDuplicatingDiagram(true);
    try {
      const response = await api.post(`/projects/${projectId}/diagrams/${diagram.diagramId}/duplicate`, {});
      const newDiagram = response.data.data as WiringDiagram;
      setDiagrams([...diagrams, newDiagram]);
    } catch (err) {
      logger.error('Failed to duplicate diagram:', err);
    } finally {
      setIsDuplicatingDiagram(false);
    }
  };

  const openDeleteDiagramModal = (diagram: WiringDiagram, e: React.MouseEvent) => {
    e.stopPropagation();
    setDiagramToAction(diagram);
    setShowDeleteDiagramModal(true);
  };

  const openRenameDiagramModal = (diagram: WiringDiagram, e: React.MouseEvent) => {
    e.stopPropagation();
    setDiagramToAction(diagram);
    setRenameDiagramName(diagram.documentTitle);
    setShowRenameDiagramModal(true);
  };

  return (
    <div className="space-y-4">
      {/* Header with New Diagram button */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          AI-assisted wiring diagrams for this project
        </p>
        <Button
          onClick={() => setShowNewDiagramModal(true)}
          variant="info"
          size="sm"
        >
          + New Diagram
        </Button>
      </div>
      
      {/* Diagram list */}
      {loadingDiagrams ? (
        <div className="text-center py-8">
          <div className="animate-spin text-2xl">⏳</div>
        </div>
      ) : diagrams.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <div className="text-4xl mb-3">🔌</div>
          <p>No wiring diagrams yet</p>
          <p className="text-sm mt-1">
            Create your first diagram to start designing
          </p>
          <Button
            onClick={() => setShowNewDiagramModal(true)}
            variant="info"
            size="sm"
            className="mt-4"
          >
            Create First Diagram
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {diagrams.map(diagram => (
            <div
              key={diagram.diagramId}
              className="p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
              onClick={() => handleDiagramClick(diagram)}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🔌</span>
                    <span className="text-sm font-medium text-foreground">
                      {diagram.documentTitle}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {diagram.diagramId}
                  </p>
                  {diagram.description && (
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {diagram.description}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* Action buttons */}
                  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                    <Button
                      onClick={(e) => openRenameDiagramModal(diagram, e)}
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-link-hover"
                      title="Rename"
                    >
                      <span className="text-sm">✏️</span>
                    </Button>
                    <Button
                      onClick={(e) => { e.stopPropagation(); handleDuplicateDiagram(diagram); }}
                      disabled={isDuplicatingDiagram}
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-success"
                      title="Duplicate"
                    >
                      <span className="text-sm">📋</span>
                    </Button>
                    {diagram.status !== 'Released' && (
                      <Button
                        onClick={(e) => openDeleteDiagramModal(diagram, e)}
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        title="Delete"
                      >
                        <span className="text-sm">🗑️</span>
                      </Button>
                    )}
                  </div>
                  {/* Status and info */}
                  <div className="flex flex-col items-end gap-1 ml-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      diagram.status === 'Released'
                        ? 'bg-success-light text-success'
                        : 'bg-warning-light text-warning'
                    }`}>
                      {diagram.status}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {diagram.deviceCount} devices • {diagram.wireCount} wires
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* New Diagram Modal */}
      <Dialog open={showNewDiagramModal} onOpenChange={(open) => {
        if (!open) {
          setShowNewDiagramModal(false);
          setNewDiagramName('');
          setNewDiagramDescription('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New Wiring Diagram</DialogTitle>
            <DialogDescription>
              Create a new AI-assisted wiring diagram for this project.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="diagram-name">Diagram Name *</Label>
              <Input
                id="diagram-name"
                value={newDiagramName}
                onChange={(e) => setNewDiagramName(e.target.value)}
                placeholder="e.g., Main Power Distribution"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="diagram-description">Description (optional)</Label>
              <Textarea
                id="diagram-description"
                value={newDiagramDescription}
                onChange={(e) => setNewDiagramDescription(e.target.value)}
                placeholder="Brief description of this diagram..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Diagram Standard *</Label>
              <div className="space-y-2">
                {([
                  { value: 'boeing_ata' as const, label: 'Boeing WDM', desc: 'Boeing Wiring Diagram Manual standard symbols' },
                  { value: 'airbus_ata' as const, label: 'Airbus ATA', desc: 'Airbus ATA chapter standard symbols' },
                  { value: 'default' as const, label: 'Debug', desc: 'Colorful debug style with visible routing paths' },
                ] as const).map(opt => (
                  <div 
                    key={opt.value}
                    onClick={() => setNewDiagramStyle(opt.value)}
                    className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                      newDiagramStyle === opt.value 
                        ? 'border-info bg-info/10' 
                        : 'border-border hover:bg-accent'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      newDiagramStyle === opt.value ? 'border-info' : 'border-muted-foreground'
                    }`}>
                      {newDiagramStyle === opt.value && <div className="w-2 h-2 rounded-full bg-info" />}
                    </div>
                    <div>
                      <div className="font-medium text-sm">{opt.label}</div>
                      <div className="text-xs text-muted-foreground">{opt.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setShowNewDiagramModal(false);
                setNewDiagramName('');
                setNewDiagramDescription('');
                setNewDiagramStyle('boeing_ata');
              }}
              variant="outline"
              disabled={creatingDiagram}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateDiagram}
              disabled={!newDiagramName.trim() || creatingDiagram}
              variant="info"
            >
              {creatingDiagram ? 'Creating...' : 'Create Diagram'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Wiring Diagram Modal */}
      <Dialog open={showDeleteDiagramModal && !!diagramToAction} onOpenChange={(open) => {
        if (!open) {
          setShowDeleteDiagramModal(false);
          setDiagramToAction(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Wiring Diagram</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this diagram? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {diagramToAction && (
            <div className="py-4">
              <div className="bg-muted/30 rounded-lg p-3 text-sm space-y-2">
                <div>
                  <strong>Diagram:</strong> {diagramToAction.documentTitle}
                </div>
                <div>
                  <strong>ID:</strong> {diagramToAction.diagramId}
                </div>
                <div>
                  <strong>Devices:</strong> {diagramToAction.deviceCount} | <strong>Wires:</strong> {diagramToAction.wireCount}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={() => {
                setShowDeleteDiagramModal(false);
                setDiagramToAction(null);
              }}
              variant="outline"
              disabled={isDeletingDiagram}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteDiagram}
              disabled={isDeletingDiagram}
              variant="destructive"
            >
              {isDeletingDiagram ? 'Deleting...' : 'Delete Diagram'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Rename Wiring Diagram Modal */}
      <Dialog open={showRenameDiagramModal && !!diagramToAction} onOpenChange={(open) => {
        if (!open) {
          setShowRenameDiagramModal(false);
          setDiagramToAction(null);
          setRenameDiagramName('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rename Diagram</DialogTitle>
            <DialogDescription>
              Enter a new name for this wiring diagram.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2">
              <Label htmlFor="rename-diagram">Diagram Name</Label>
              <Input
                id="rename-diagram"
                value={renameDiagramName}
                onChange={(e) => setRenameDiagramName(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setShowRenameDiagramModal(false);
                setDiagramToAction(null);
                setRenameDiagramName('');
              }}
              variant="outline"
              disabled={isRenamingDiagram}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRenameDiagram}
              disabled={!renameDiagramName.trim() || isRenamingDiagram}
            >
              {isRenamingDiagram ? 'Renaming...' : 'Rename'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
